import { createRouter, createWebHistory } from 'vue-router'
import { useSessionStore } from '@loczi/auth'

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/login', name: 'login', component: () => import('./views/LoginView.vue'), meta: { public: true } },
    // Folha de impressão do pedido — fora do AdminShell para sair limpa no papel.
    { path: '/orders/:id/print', name: 'order-print', component: () => import('./views/OrderPrintView.vue') },
    {
      path: '/',
      component: () => import('./layouts/AdminShell.vue'),
      children: [
        { path: '', name: 'dashboard', component: () => import('./views/DashboardView.vue') },
        { path: 'impersonate', name: 'impersonate', component: () => import('./views/ImpersonateView.vue') },
        { path: 'orders', name: 'orders', component: () => import('./views/OrdersView.vue') },
        { path: 'orders/new', name: 'order-new', component: () => import('./views/OrderCreateView.vue') },
        { path: 'customers', name: 'customers', component: () => import('./views/CustomersView.vue') },
        { path: 'customers/:id', name: 'customer-detail', component: () => import('./views/CustomerDetailView.vue') },
        { path: 'customers/:id/edit', name: 'customer-edit', component: () => import('./views/CustomerEditView.vue') },
        { path: 'leads', name: 'leads', component: () => import('./views/LeadsView.vue') },
        { path: 'blocklist', name: 'blocklist', component: () => import('./views/BlocklistView.vue') },
        { path: 'orders/:id', name: 'order-detail', component: () => import('./views/OrderDetailView.vue') },
        { path: 'payments', name: 'payments', component: () => import('./views/PaymentsView.vue') },
        { path: 'financeiro', name: 'financial', component: () => import('./views/FinancialView.vue') },
        { path: 'subscriptions', name: 'subscriptions', component: () => import('./views/SubscriptionsView.vue') },
        { path: 'shipments', name: 'shipments', component: () => import('./views/ShipmentsView.vue') },
        { path: 'suppliers', name: 'suppliers', component: () => import('./views/SuppliersView.vue') },
        { path: 'products', name: 'products', component: () => import('./views/ProductsView.vue') },
        { path: 'product-groups', name: 'product-groups', component: () => import('./views/ProductGroupsView.vue') },
        { path: 'products/new', name: 'product-new', component: () => import('./views/ProductFormView.vue') },
        { path: 'products/:id/edit', name: 'product-edit', component: () => import('./views/ProductFormView.vue') },
        { path: 'devices', name: 'devices', component: () => import('./views/DevicesView.vue') },
        { path: 'telemetry', name: 'telemetry', component: () => import('./views/TelemetryView.vue') },
        { path: 'integrations', name: 'integrations', component: () => import('./views/IntegrationsView.vue') },
        { path: 'backup', name: 'backup', component: () => import('./views/BackupView.vue') },
        { path: 'audit', name: 'audit', component: () => import('./views/AuditView.vue') },
        { path: 'terms', name: 'terms', component: () => import('./views/TermsView.vue') },
        { path: 'terms/acceptances', name: 'term-acceptances', component: () => import('./views/TermAcceptancesView.vue') },
        { path: 'sellers', name: 'sellers', component: () => import('./views/SellersView.vue') },
        { path: 'sellers/:id', name: 'seller-detail', component: () => import('./views/SellerDetailView.vue') },
        { path: 'commissions', name: 'commissions', component: () => import('./views/CommissionsView.vue') },
      ],
    },
    // Área do vendedor (SPEC-VENDEDORES) — CRM interno, propostas e comissões.
    {
      path: '/vendedor',
      component: () => import('./layouts/SellerShell.vue'),
      meta: { sellerArea: true },
      children: [
        { path: '', name: 'seller-dashboard', component: () => import('./views/seller/DashboardView.vue') },
        { path: 'propostas', name: 'seller-proposals', component: () => import('./views/seller/ProposalsView.vue') },
        { path: 'comissoes', name: 'seller-commissions', component: () => import('./views/seller/CommissionsView.vue') },
        { path: 'clientes', name: 'seller-clients', component: () => import('./views/seller/ClientsView.vue') },
      ],
    },
  ],
})

const webUrl = import.meta.env.VITE_WEB_URL ?? 'http://localhost:5173'

router.beforeEach(async (to) => {
  const session = useSessionStore()
  if (!session.ready) await session.bootstrap()
  const isSeller = Boolean(session.user?.seller?.active)
  // Conta de cliente autenticada no app admin → o painel dela é o web.
  // Exceções: Super Admin impersonando fica aqui; vendedor ativo usa /vendedor.
  if (session.isAuthenticated && !session.user!.is_super_admin && !session.user!.is_impersonating && !isSeller) {
    const rt = to.query.return_to
    const target = typeof rt === 'string' && rt.startsWith('/') && !rt.startsWith('//') ? rt : '/'
    window.location.href = webUrl.replace(/\/$/, '') + target
    return false
  }
  if (to.meta.public) {
    if (session.isAuthenticated && to.name === 'login') {
      return isSeller && !session.user!.is_super_admin ? { name: 'seller-dashboard' } : { name: 'dashboard' }
    }
    return true
  }
  if (!session.isAuthenticated) return { name: 'login' }
  // Separação de superfícies: vendedor (sem super admin) só vê /vendedor;
  // super admin navega nas duas, mas a home dele é a operação.
  if (isSeller && !session.user!.is_super_admin) {
    if (to.matched.some((r) => r.meta.sellerArea)) return true
    return { name: 'seller-dashboard' }
  }
  if (to.matched.some((r) => r.meta.sellerArea)) return { name: 'dashboard' }
  return true
})
